import React, { FC, ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';

import Echart from '../../../components/eChart/Echart';
import { environment } from '../../../environments/environment';
import { useDesignSystemVar } from '../../hooks/useDesignSystemVar';
import { parser } from './helpers/parser';
import { useElementOnScreen } from './helpers/useElementOnScreen';

const environmentLink: string | undefined = environment().assetsLink;
const ChartStyled = styled.div<ChartStyledProps>`
  position: relative;
  width: 100%;
  opacity: ${(props) => (props.opacity !== 0 ? '1' : 0)};
  transition: opacity 0.2s ease-in-out;
  .chart__wrapper {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: ${(props) => props.horizontalAlignment || 'inherit'};
    align-items: center;
  }
  .echarts-for-react {
    width: 100%;
  }
`;

type ChartProps = {
  width: string;
  horizontalAlignment: string;
  chartType: string;
  data: any;
  color: string;
  title: string;
  prefix: string;
  suffix: string;
  anchorTrigger: string;
  showLegend: boolean;
  showGridlines: boolean;
  showAnnotation: boolean;
  annotationPosition: string;
  showAxisLabels: boolean;
  legendPosition: string;
  lineSize: string;
  donut: boolean;
  smooth: boolean;
  height: number;
  animation: boolean;
  donutSize: number;
  editorPlugins: Record<string, any> | undefined;
  emitAction: Function;
};

type ChartStyledProps = {
  horizontalAlignment: string;
  opacity: number;
};

const Chart: FC<ChartProps> = ({ ...props }) => {
  const fontFamily: string | undefined = useDesignSystemVar('var(--paragraph-font)');
  const option: { option: { [key: string]: unknown } } = parser(props.chartType, {
    ...props,
    fontFamily,
  });
  const [containerRef, key] = useElementOnScreen({ threshold: 0.7 }, props.animation);

  const [width, setWidth] = useState<string>(props.width || '100');
  const [height, setHeight] = useState<number>(props.height);
  const [elementDomWidth, setElementDomWidth] = useState<number>(
    containerRef?.current?.clientWidth,
  );
  const [chartContainerWidth, setChartContainerWidth] = useState<number>(0);
  const [chartContainerHeight, setChartContainerHeight] = useState<number>(0);
  const editorPlugins = props.editorPlugins;
  const ResizeAxis = editorPlugins?.ResizeAxis;
  const firstRender = editorPlugins?.useFirstRender();

  const actionFunction = props?.emitAction;
  useEffect(() => {
    if (actionFunction && editorPlugins && !firstRender) actionFunction({ action: 'select' });
  }, [props.chartType]);

  useEffect(() => {
    if (containerRef && containerRef.current) setElementDomWidth(containerRef.current.clientWidth);
    if (containerRef && containerRef.current) {
      const chart: HTMLDivElement | null = containerRef.current.querySelector('.echarts-for-react');
      if (!chart) return;
      setChartContainerWidth(chart.clientWidth);
      setChartContainerHeight(chart.clientHeight);
    }
  }, [width, height]);

  useEffect(() => {
    setWidth(props.width);
    setHeight(props.height);
  }, [props.width, props.height]);

  const emitWidth = (width: string): void => setWidth(width);
  const emitHeight = (height: number): void => setHeight(height);

  return (
    <ChartStyled ref={containerRef} opacity={key} horizontalAlignment={props.horizontalAlignment}>
      <div className="chart__wrapper">
        {ResizeAxis && (
          <ResizeAxis
            widthProps={{
              width,
              property: 'width',
              min: 10,
              max: 100,
              elementDomWidth,
              containerWidth: chartContainerWidth,
              emitWidth,
            }}
            heightProps={{
              height,
              property: 'height',
              containerHeight: chartContainerHeight,
              emitHeight,
            }}
          />
        )}
        <Echart option={option.option} trigger={key} height={height} width={width} />
      </div>
    </ChartStyled>
  );
};

//element options
const options = [
  {
    name: 'data',
    title: 'Edit data',
    icon: environmentLink + '/assets/images/Options/edit-white.svg',
    action: 'setActiveSetting',
    settingTitle: 'Edit Data',
  },
  {
    name: 'ChartLayout',
    title: 'Settings',
    icon: environmentLink + '/assets/images/Options/settings.svg',
    action: 'setActiveSetting',
    settingTitle: 'Layout',
  },
];

export default {
  Chart,
  options,
};
